import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, of, map, catchError } from 'rxjs';
import { environment } from '../../environments/environment';
import { User } from '../interfaces/user.interface';
import { DeveloperApplication } from '../interfaces/application.interface';

interface AuthMeResponse {
  user: User;
  application: DeveloperApplication | null;
}

interface CallbackResponse {
  token: string;
  user: User;
  application_status: string | null;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private currentUserSubject = new BehaviorSubject<User | null>(null);
  private applicationSubject = new BehaviorSubject<DeveloperApplication | null>(null);
  private initializedSubject = new BehaviorSubject<boolean>(false);

  currentUser$ = this.currentUserSubject.asObservable();
  application$ = this.applicationSubject.asObservable();
  initialized$ = this.initializedSubject.asObservable();
  isAuthenticated$ = this.currentUser$.pipe(map(user => !!user));
  isApproved$ = this.application$.pipe(map(app => app?.status === 'approved'));

  constructor(private http: HttpClient, private router: Router) {
    this.loadUser();
  }

  private loadUser(): void {
    const token = localStorage.getItem('developers_token');
    if (token) {
      this.getMe().subscribe({
        next: () => this.initializedSubject.next(true),
        error: () => {
          localStorage.removeItem('developers_token');
          this.currentUserSubject.next(null);
          this.applicationSubject.next(null);
          this.initializedSubject.next(true);
        },
      });
    } else {
      this.initializedSubject.next(true);
    }
  }

  getMe(): Observable<AuthMeResponse> {
    return this.http.get<AuthMeResponse>(`${environment.apiUrl}/auth/me`).pipe(
      map(response => {
        this.currentUserSubject.next(response.user);
        this.applicationSubject.next(response.application);
        return response;
      }),
      catchError(error => {
        if (error.status === 401 || error.status === 403) {
          localStorage.removeItem('developers_token');
          this.currentUserSubject.next(null);
          this.applicationSubject.next(null);
        }
        throw error;
      }),
    );
  }

  async initiateOIDCLogin(): Promise<void> {
    const codeVerifier = this.generateCodeVerifier();
    const codeChallenge = await this.generateCodeChallenge(codeVerifier);
    const state = this.generateRandomString(32);
    const nonce = this.generateRandomString(32);

    sessionStorage.setItem('oidc_code_verifier', codeVerifier);
    sessionStorage.setItem('oidc_state', state);
    sessionStorage.setItem('oidc_nonce', nonce);

    const params = new URLSearchParams({
      response_type: 'code',
      client_id: environment.oidcClientId,
      redirect_uri: environment.oidcRedirectUri,
      scope: 'openid profile email',
      state,
      nonce,
      code_challenge: codeChallenge,
      code_challenge_method: 'S256',
    });

    window.location.href = `${environment.oidcAuthorizeUrl}?${params.toString()}`;
  }

  handleCallback(code: string, state: string): Observable<CallbackResponse> {
    const savedState = sessionStorage.getItem('oidc_state');
    const codeVerifier = sessionStorage.getItem('oidc_code_verifier');

    sessionStorage.removeItem('oidc_state');
    sessionStorage.removeItem('oidc_nonce');
    sessionStorage.removeItem('oidc_code_verifier');

    if (state !== savedState) {
      throw new Error('State mismatch — possible CSRF attack');
    }

    if (!codeVerifier) {
      throw new Error('Missing code_verifier — PKCE flow broken');
    }

    return this.http.post<CallbackResponse>(`${environment.apiUrl}/auth/callback`, {
      code,
      code_verifier: codeVerifier,
    }).pipe(
      map(response => {
        localStorage.setItem('developers_token', response.token);
        this.currentUserSubject.next(response.user);
        return response;
      }),
    );
  }

  logout(): void {
    const token = localStorage.getItem('developers_token');
    if (token) {
      this.http.post(`${environment.apiUrl}/auth/logout`, {}).subscribe();
    }
    localStorage.removeItem('developers_token');
    this.currentUserSubject.next(null);
    this.applicationSubject.next(null);
    this.router.navigate(['/']);
  }

  getToken(): string | null {
    return localStorage.getItem('developers_token');
  }

  private generateCodeVerifier(): string {
    return this.generateRandomString(64);
  }

  private async generateCodeChallenge(verifier: string): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(verifier);
    const digest = await crypto.subtle.digest('SHA-256', data);
    return this.base64UrlEncode(digest);
  }

  private base64UrlEncode(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (const byte of bytes) {
      binary += String.fromCharCode(byte);
    }
    return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  }

  private generateRandomString(length: number): string {
    const array = new Uint8Array(length);
    crypto.getRandomValues(array);
    return Array.from(array, byte => byte.toString(36).padStart(2, '0')).join('').substring(0, length);
  }
}
